<script setup lang="ts">
import LeBell from "@dsk/icons/LeBell.vue";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import LeSearchSwitchedDictionarySettingToast from "@/components/toasts/LeSearchSwitchedDictionarySettingToast.vue";
import LeSearchSwitchedDictionaryToast from "@/components/toasts/LeSearchSwitchedDictionaryToast.vue";
import { MatomoHelper, SearchRouteHelper } from "@/helpers";
import { Cookies } from "@/services";
import { useUserStore } from "@/stores";
import { type Dictionary, MatomoEventAction, MatomoEventCategory } from "@/types";

const props = defineProps<{
  selectedDictionary: Dictionary;
  switchedFromDictionary: Dictionary;
  searchedDictionaries: Dictionary[];
}>();

const toast = useToast();
const i18n = useI18n();

const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

const TOAST_ID = "SWITCHED_DICTIONARY_TOAST";

const COOKIE_KEY = "show_switched_dictionary_toast";
const COOKIE_VALUE = "false";

const showSettingToast = ref<boolean>(Cookies.get(COOKIE_KEY) !== COOKIE_VALUE);

const showToasts = computed<boolean>(() => {
  if (!showSettingToast.value) {
    return false;
  }

  // Only show the toast if we have actually searched the dictionary that the user is in
  return props.searchedDictionaries.includes(props.switchedFromDictionary);
});

const renderInfoToast = () => {
  toast.update(
    TOAST_ID,
    {
      content: {
        component: LeSearchSwitchedDictionaryToast,
        props: {
          i18n,
          selectedDictionary: props.selectedDictionary,
          switchedFromDictionary: props.switchedFromDictionary
        },
        listeners: {
          revertClick: (toDictionary: Dictionary) => {
            searchRouteHelper.selectDictionary(toDictionary.id);
          },
          showSettingClick: () => {
            renderSettingToast();
          }
        }
      },
      options: {
        timeout: 20000,
        icon: LeBell
      }
    },
    true
  );
};

const renderSettingToast = () => {
  toast.update(TOAST_ID, {
    content: {
      component: LeSearchSwitchedDictionarySettingToast,
      props: { i18n },
      listeners: {
        cancelClick: () => {
          renderInfoToast();
        },
        disableToastClick: () => {
          const expires = new Date();
          expires.setFullYear(expires.getFullYear() + 1);

          Cookies.set(COOKIE_KEY, COOKIE_VALUE, { expires });
          showSettingToast.value = false;
        }
      }
    },
    options: {
      timeout: 0,
      icon: null
    }
  });
};

watch(
  showToasts,
  (showToast) => {
    if (!showToast) {
      return;
    }

    toast.clear();
    renderInfoToast();

    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.ToastDisplayed,
      "Change of dictionary toast displayed"
    );
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  toast.clear();
});
</script>

<template>
  <!-- Empty template, as toasts gets injected into the DOM -->
  <span class="le-switched-dictionary-toast-wrapper"></span>
</template>

<style lang="less" scoped>
.le-switched-dictionary-toast-wrapper {
  display: none;
}
</style>
